import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	INVOICE_QUERY_KEY,
	PROJECTS_INVOICES_QUERY_KEY,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import CreateReversalInvoiceForm
	from "~/modules/billing/components/InvoiceDetailsView/components/CreateReversalInvoiceSidebar/components/CreateReversalInvoiceForm";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	originalInvoiceData: Invoice;
};

const CreateReversalInvoiceSidebar = ({
	isOpen,
	onClose,
	originalInvoiceData,
}: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async (reversalInvoiceId: string) => {
		queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: INVOICE_QUERY_KEY(originalInvoiceData.id) });
		if (originalInvoiceData.projectId) {
			queryClient.invalidateQueries({ queryKey: PROJECTS_INVOICES_QUERY_KEY(originalInvoiceData.projectId) });
		}

		navigate(generatePath(INVOICE_DETAILS_PATH, { invoiceId: reversalInvoiceId }));
		onClose();
	}, [navigate, onClose, originalInvoiceData.id, originalInvoiceData.projectId, queryClient]);

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={() => onClose()}>
			{isOpen && <CreateReversalInvoiceForm onCancel={onClose}
												  onSuccess={handleSuccess}
												  originalInvoiceData={originalInvoiceData} />}
		</Sidebar>
	);
};

export default CreateReversalInvoiceSidebar;
